/* global runtime */
// Koblinger mot betaling, oppgjør, MCP, Slack og nettbutikk.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const getInboundReceiptMailbox = async (businessId = runtime.activeBusinessId) => {
  const payload = await authorizedPost('getInboundReceiptMailboxHttp', { businessId });
  return payload.mailbox || null;
};

const rotateInboundReceiptMailbox = async (businessId = runtime.activeBusinessId) => {
  const payload = await authorizedPost('rotateInboundReceiptMailboxHttp', { businessId });
  return payload.mailbox || null;
};
const getInboundInvestmentMailbox = async (businessId = runtime.activeBusinessId) => {
  const payload = await authorizedPost('getInboundReceiptMailboxHttp', { businessId, purpose: 'investment' });
  return payload.mailbox || null;
};

const createCheckoutSession = async (plan) => {
  const payload = await authorizedPost('createCheckoutSessionHttp', { plan });
  if (!payload.url) throw new Error(t('svc.missingPaymentLink'));
  return payload.url;
};

const createCustomerPortalSession = async () => {
  const payload = await authorizedPost('createCustomerPortalSessionHttp');
  if (!payload.url) throw new Error(t('svc.missingPortalLink'));
  return payload.url;
};

const stripeConnectIntegrationAction = async (action) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('stripeConnectIntegrationHttp', {
    businessId: runtime.activeBusinessId,
    action,
  });
};

const getStripeConnectIntegrationStatus = async () => {
  const payload = await stripeConnectIntegrationAction('status');
  return payload?.integration || { connected: false };
};

const beginStripeConnectIntegration = async () => {
  const payload = await stripeConnectIntegrationAction('connect');
  if (!payload?.authorizeUrl) throw new Error(t('svc.stripeMissingAuthorizeUrl'));
  return payload.authorizeUrl;
};

const disconnectStripeConnectIntegration = async () => stripeConnectIntegrationAction('disconnect');

const zapierIntegrationAction = async (action) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('zapierIntegrationHttp', {
    clientId: runtime.activeBusinessId,
    action,
  });
};

const getZapierIntegrationStatus = async () => {
  const payload = await zapierIntegrationAction('status');
  return payload?.integration || { connected: false };
};

const generateZapierApiKey = async () => {
  const payload = await zapierIntegrationAction('generate_key');
  if (!payload?.apiKey) throw new Error(t('svc.zapierMissingApiKey'));
  return payload;
};

const disconnectZapierIntegration = async () => zapierIntegrationAction('disconnect');

// Plumme som regnskapsmal: nokkelen er utstedt per kildesystem, slik at
// oppgjorene lander pa riktig leverandor og egen oppgjorskonto.
const partnerSettlementIntegrationAction = async (provider, action) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('partnerSettlementIntegrationHttp', {
    clientId: runtime.activeBusinessId,
    provider,
    action,
  });
};

const getPartnerSettlementIntegrationStatus = async (provider) => {
  const payload = await partnerSettlementIntegrationAction(provider, 'status');
  return payload?.integration || { connected: false };
};

const generatePartnerSettlementApiKey = async (provider) => {
  const payload = await partnerSettlementIntegrationAction(provider, 'generate_key');
  if (!payload?.apiKey) throw new Error(t('svc.settlementMissingApiKey'));
  return payload;
};

const disconnectPartnerSettlementIntegration = async (provider) => partnerSettlementIntegrationAction(provider, 'disconnect');

// Adressen leverandoren skal sende oppgjor til. Bygges i service-laget slik at
// UI-et slipper a kjenne til Firebase-oppsettet.
const getPartnerSettlementApiUrl = () => functionUrl('partnerSettlementApiHttp');

const listPartnerSettlementBatches = async (provider) => partnerSettlementIntegrationAction(provider, 'batches');
const approvePartnerSettlementBatch = async (provider, batchId) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('partnerSettlementIntegrationHttp', {
    clientId: runtime.activeBusinessId,
    provider,
    action: 'approve_batch',
    batchId,
  });
};

// Gjennomgang av en stoppet oppgjorsbatch. Samme kontrakt for alle tre
// integrasjonsfamiliene, med leverandoren som ruter til riktig funksjon.
const commerceBatchFunction = (provider) => {
  if (window.PARTNER_SETTLEMENT_PROVIDERS?.[provider]) return 'partnerSettlementIntegrationHttp';
  return provider === 'shopify' ? 'shopifyIntegrationHttp' : 'wooCommerceIntegrationHttp';
};

const commerceBatchAction = async (provider, action, fields = {}) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  const isPartnerSettlement = Boolean(window.PARTNER_SETTLEMENT_PROVIDERS?.[provider]);
  return authorizedPost(commerceBatchFunction(provider), {
    ...(isPartnerSettlement ? { clientId: runtime.activeBusinessId, provider } : { businessId: runtime.activeBusinessId }),
    action,
    ...fields,
  });
};

const resolveCommerceBatch = async (provider, batchId, resolutions, note) =>
  commerceBatchAction(provider, 'resolve_batch', { batchId, resolutions, note });

const dismissCommerceBatch = async (provider, batchId, note) =>
  commerceBatchAction(provider, 'dismiss_batch', { batchId, note });

const approveCommerceBatch = async (provider, batchId) => commerceBatchAction(provider, 'approve_batch', { batchId });

// Samlet dagsoppgjorsliste pa tvers av leverandorer, for «Dagsoppgjor»-siden og
// paminnelsen pa forsiden.
const listCommerceSettlements = async () => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('commerceSettlementsHttp', { businessId: runtime.activeBusinessId, action: 'list' });
};

const mcpAiIntegrationAction = async (action, fields = {}) => {
  const businessId = fields.businessId || runtime.activeBusinessId;
  return authorizedPost('mcpAiIntegrationHttp', {
    ...fields,
    ...(businessId ? { businessId } : {}),
    action,
  });
};

const getMcpAiIntegrationStatus = async (provider) => {
  const payload = await mcpAiIntegrationAction('status', { provider });
  return {
    available: payload?.available === true,
    mcpUrl: payload?.mcpUrl || 'https://app.plumme.no/mcp',
    integration: payload?.integration || { connected: false },
  };
};

const disconnectMcpAiIntegration = async (provider) => mcpAiIntegrationAction('disconnect', { provider });

const getMcpAuthorizationRequest = async (requestId) => {
  const payload = await mcpAiIntegrationAction('authorization_request', { requestId, businessId: '' });
  return payload?.authorizationRequest || null;
};

const approveMcpAuthorizationRequest = async ({ requestId, businessId = runtime.activeBusinessId } = {}) => {
  const payload = await mcpAiIntegrationAction('approve_authorization', { requestId, businessId });
  if (!payload?.redirectUrl) throw new Error(t('svc.mcpMissingRedirect'));
  return payload.redirectUrl;
};

const denyMcpAuthorizationRequest = async (requestId) => {
  const payload = await mcpAiIntegrationAction('deny_authorization', { requestId, businessId: '' });
  if (!payload?.redirectUrl) throw new Error(t('svc.mcpMissingRedirect'));
  return payload.redirectUrl;
};

const slackIntegrationAction = async (action, fields = {}) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('slackIntegrationHttp', {
    businessId: runtime.activeBusinessId,
    action,
    ...fields,
  });
};

const getSlackIntegrationStatus = async () => {
  const payload = await slackIntegrationAction('status');
  return payload?.integration || { connected: false };
};

const beginSlackIntegration = async () => {
  const payload = await slackIntegrationAction('connect');
  if (!payload?.authorizeUrl) throw new Error(t('svc.slackMissingAuthorizeUrl'));
  return payload.authorizeUrl;
};

const sendSlackTestMessage = async () => slackIntegrationAction('test');
const saveSlackNotificationPreferences = async (notificationPreferences) => {
  const payload = await slackIntegrationAction('update_preferences', { notificationPreferences });
  return payload?.notificationPreferences;
};
const disconnectSlackIntegration = async () => slackIntegrationAction('disconnect');

const shopifyIntegrationAction = async (action, fields = {}) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('shopifyIntegrationHttp', {
    businessId: runtime.activeBusinessId,
    action,
    ...fields,
  });
};

const getShopifyIntegrationStatus = async () => {
  const payload = await shopifyIntegrationAction('status');
  return payload?.integration || { connected: false };
};

const beginShopifyIntegration = async (shop) => {
  const payload = await shopifyIntegrationAction('connect', { shop });
  if (!payload?.authorizeUrl) throw new Error(t('svc.shopifyMissingAuthorizeUrl'));
  return payload.authorizeUrl;
};

const syncShopifyIntegration = async () => shopifyIntegrationAction('sync');
const listShopifyCommerceBatches = async () => shopifyIntegrationAction('batches');
const approveShopifyCommerceBatch = async (batchId) => shopifyIntegrationAction('approve_batch', { batchId });
const disconnectShopifyIntegration = async () => shopifyIntegrationAction('disconnect');

const wooCommerceIntegrationAction = async (action, fields = {}) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  return authorizedPost('wooCommerceIntegrationHttp', {
    businessId: runtime.activeBusinessId,
    action,
    ...fields,
  });
};

const getWooCommerceIntegrationStatus = async () => {
  const payload = await wooCommerceIntegrationAction('status');
  return payload?.integration || { connected: false };
};

const connectWooCommerceIntegration = async ({ storeUrl, consumerKey, consumerSecret }) => {
  const payload = await wooCommerceIntegrationAction('connect', { storeUrl, consumerKey, consumerSecret });
  return payload?.integration || { connected: true };
};

const syncWooCommerceIntegration = async () => wooCommerceIntegrationAction('sync');
const listWooCommerceCommerceBatches = async () => wooCommerceIntegrationAction('batches');
const approveWooCommerceCommerceBatch = async (batchId) => wooCommerceIntegrationAction('approve_batch', { batchId });
const disconnectWooCommerceIntegration = async () => wooCommerceIntegrationAction('disconnect');

// Samlet, sanert status for alle integrasjonsdokumenter pa forretningen, slik
// at katalog-plassholderne kan speile admin-aktivering uten egen backend.
const getBusinessIntegrationsStatus = async () => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('businessIntegrationsStatusHttp', {
    businessId: runtime.activeBusinessId,
  });
  return payload?.integrations || {};
};

const mcpActionApproval = (fields) => authorizedPost('mcpActionApprovalHttp', fields);
